import React, { useContext, useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { cityList, initSideModal, urlRegEx } from 'helpers/utils';
import PrimaryButtom from 'components/common/buttons/primary-btn';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import Select from 'components/common/select';
import InputField from 'components/common/input-field';
import { setSideModalData } from 'store/features/modelSlice';
import { IClientUser } from 'store/features/userSlice';
import { emailRegEx, onlyNumbers, countryList, stateList, scrollUp } from 'helpers/utils';
import PhoneInputComponent from 'components/common/phone-input';
import {
  AddOrganizationAPI,
  EditOrganizationAPI,
  IAddOrganization,
  IEditOrganizationParkinfParams,
  IEditOrganizationRequest,
  IOrganizationAddress,
  IOrganizationDetails,
  IOrganizations,
  IUploadImageRequestParam,
  getOrganizationData,
  getOrganizationUploadLogoLocation,
  editOrganizationParkingDetails,
  removeOrganizationParking,
  addOrganizationParkingDetails,
} from 'store/features/organizationSlice';
import toast from 'react-hot-toast';
import { AppContext } from 'context/app-context';
import { ReactComponent as EditIcon } from 'assets/icon/pencil.svg';
import { ReactComponent as DeleteIcon } from 'assets/icon/trash.svg';
import CommonLayout from './common-layout';
import { IAddress } from 'helpers/types';
import { useTranslation } from 'react-i18next';

export interface IOrganizationError
  extends Omit<IOrganizations, '_id' | 'fiscal_address' | 'merchant_detail'> {
  fiscal_address: string;
  key: string;
  secret: string;
  resource: string;
}
export interface IAddClientUserRequest extends Omit<IClientUser, 'date_of_birth'> {
  birth_date: string;
  organization_id: string;
  role: string;
}

export interface IParkingAddressEdited extends IAddress {
  isEdited?: boolean;
}

const orgInitData: IOrganizations = {
  _id: '',
  organization_name: '',
  logo_key: '',
  logo_url: '',
  logo: '',
  fiscal_name: '',
  fiscal_number: '',
  email: '',
  country_code: '',
  mobile_no: '',

  fiscal_address: null,
  contact_us_url: '',
  privacy_policy_url: '',
  parking_details: [],
  merchant_detail: {
    key: '',
    secret: '',
    resource: '',
  },
  database_detail: {
    name: '',
    api_key: '',
  },
};

const AddOrganization: React.FC = () => {
  const { setLoader } = useContext(AppContext);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    sideModal: {
      content: { formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { editedOrganization } = useAppSelector(state => state.organization);
  const [organization, setOrganizations] = useState<IOrganizations | IOrganizationDetails>(
    orgInitData,
  );
  const [parkingAddress, setParkingAddress] = useState<IParkingAddressEdited | null>(null);
  const [logo, setLogo] = useState<{ file?: null | File; url?: string } | null>(null);
  const [dropdownSearch, setDropdownSearch] = useState<string>('');
  const [showGeotab, setShowGeotab] = useState<boolean>(false);
  const [showCar, setShowCar] = useState<boolean>(false);
  const [error, setError] = useState<Partial<IOrganizationError>>({});
  let mob_num_length_ref = useRef(0);
  let logoRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    organization_name,
    fiscal_name,
    email,
    country_code,
    mobile_no,
    fiscal_number,

    fiscal_address,
  } = organization;
  const getOrganizationDetails = async () => {
    setLoader(true);
    dispatch(getOrganizationData({ org_id: editedOrganization?._id || '' }))
      .then((data: IOrganizationDetails) => {
        setOrganizations({ ...data, logo_key: data?.logo_data?.url });
        setLogo({ file: null, url: data?.logo_url });
        setShowCar(Boolean(data?.car_details));
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  useEffect(() => {
    if (formFor === 'EDIT') {
      getOrganizationDetails();
    }
  }, []);
  const getErrorData = () => {
    return {
      organization_name: !organization_name?.trim()
        ? t('organization.errors.organization_name')
        : '',
      email: !email?.trim()
        ? t('organization.errors.email_required')
        : !emailRegEx.test(email)
        ? t('organization.errors.invalid_email')
        : '',
      fiscal_name: !fiscal_name?.trim() ? t('organization.errors.fiscal_name_required') : '',
      fiscal_number: !fiscal_number?.trim() ? t('organization.errors.fiscal_number_required') : '',

      mobile_no:
        !mobile_no?.trim() || !country_code?.trim()
          ? t('organization.errors.telephone_required')
          : mob_num_length_ref.current && mobile_no?.trim().length !== mob_num_length_ref.current
          ? t('organization.errors.invalid_telephone')
          : '',
      fiscal_address:
        !fiscal_address ||
        (fiscal_address &&
          (!fiscal_address?.address1?.trim() ||
            !fiscal_address?.country?.trim() ||
            !fiscal_address?.state?.trim() ||
            !fiscal_address?.city?.trim() ||
            !fiscal_address?.zipcode))
          ? t('organization.errors.address_required')
          : '',
      contact_us_url:
        organization?.contact_us_url?.trim() && !urlRegEx.test(organization?.contact_us_url?.trim())
          ? t('organization.errors.invalid_concessionaire')
          : '',
      privacy_policy_url:
        organization?.privacy_policy_url?.trim() &&
        !urlRegEx.test(organization?.privacy_policy_url?.trim())
          ? t('organization.errors.privacy_policy_url')
          : '',
      key:
        !organization?.merchant_detail?.key?.trim() &&
        organization?.merchant_detail?.secret?.trim()
          ? t('organization.errors.key_required')
          : '',
      secret:
        !organization?.merchant_detail?.secret?.trim() &&
        organization?.merchant_detail?.key?.trim()
          ? t('organization.errors.secret_required')
          : '',
      resource: organization?.merchant_detail?.resource?.trim() &&
        !organization?.merchant_detail?.resource?.trim()
        ? t('organization.errors.resource_required')
        : '',
      car_details: showCar
        ? {
            user_name: !organization?.car_details?.user_name?.trim()
              ? t('organization.errors.car_user_name_required')
              : '',
            password: !organization?.car_details?.password?.trim()
              ? t('organization.errors.car_password_required')
              : '',
          }
        : undefined,
    };
  };
  const isValid = () => {
    if (
      !organization_name?.trim() ||
      !email?.trim() ||
      !emailRegEx.test(email) ||
      !mobile_no?.trim() ||
      !country_code?.trim() ||
      !fiscal_address ||
      (fiscal_address &&
        (!fiscal_address?.address1?.trim() ||
          !fiscal_address?.country?.trim() ||
          !fiscal_address?.state?.trim() ||
          !fiscal_address?.city?.trim() ||
          !fiscal_address?.zipcode)) ||
      (organization?.contact_us_url?.trim() &&
        !urlRegEx.test(organization?.contact_us_url?.trim())) ||
      (organization?.privacy_policy_url?.trim() &&
        !urlRegEx.test(organization?.privacy_policy_url?.trim())) ||
      (!organization?.merchant_detail?.secret?.trim() &&
        organization?.merchant_detail?.key?.trim()) ||
      (!organization?.merchant_detail?.key?.trim() &&
        organization?.merchant_detail?.secret?.trim()) ||
      (showGeotab &&
        organization?.geotab_credentials &&
        (!organization.geotab_credentials.database_name?.trim() ||
          !organization.geotab_credentials.user_name?.trim() ||
          !organization.geotab_credentials.password?.trim())) ||
      (showCar &&
        (!organization?.car_details?.user_name?.trim() ||
          !organization?.car_details?.password?.trim()))
    ) {
      scrollUp(scrollRef);
      const output = getErrorData();

      setError(output);
      return false;
    } else {
      const output = getErrorData();

      setError(output);
      return true;
    }
  };

  const handleChange = (field: string, value: string, subField: string = '') => {
    let updatedData = { ...organization };
    switch (field) {
      case 'fiscal_address': {
        const address: IOrganizationAddress = {
          address1: updatedData?.fiscal_address?.address1 || '',
          address2: updatedData?.fiscal_address?.address2 || '',
          city: updatedData?.fiscal_address?.city || '',
          state: updatedData?.fiscal_address?.state || '',
          country: updatedData?.fiscal_address?.country || '',
          zipcode: updatedData?.fiscal_address?.zipcode || '',
          [subField]: value,
        };
        updatedData = {
          ...updatedData,
          fiscal_address: address,
        };
        break;
      }
      default: {
        updatedData = { ...updatedData, [field]: value };
        break;
      }
    }
    setOrganizations(updatedData);
  };
  const handleMobileNumber = (data: { code: string; number: string; format: string }) => {
    setOrganizations({ ...organization, mobile_no: data.number, country_code: data.code });
    mob_num_length_ref.current =
      data.format.replaceAll('+', '').replaceAll(' ', '').replaceAll('-', '').length -
      data.code.length;
  };
  const handleAddOrganization = () => {
    setLoader(true);
    const parkings = organization?.parking_details.map(pa => {
      return pa?._id || '';
    });
    if (!organization?.fiscal_address?.address2?.trim()) {
      delete organization?.fiscal_address?.address2;
    }
    let request: IAddOrganization = {
      organization_detail: {
        organization_name: organization?.organization_name || '',
        fiscal_name: organization?.fiscal_name,
        fiscal_number: organization?.fiscal_number,
        email: organization?.email,
        country_code: `+${organization?.country_code.replaceAll('+', '')}`,
        mobile_no: organization?.mobile_no,
        fiscal_address: organization?.fiscal_address || null,
        parking_ids: parkings,
      },
    };

    if (organization?.logo_key?.trim()) {
      request = {
        ...request,
        organization_detail: { ...request.organization_detail, logo: organization?.logo_key },
      };
    }
    if (organization?.contact_us_url?.trim()) {
      request = {
        ...request,
        organization_detail: {
          ...request.organization_detail,
          contact_us_url: organization?.contact_us_url,
        },
      };
    }
    if (organization?.privacy_policy_url?.trim()) {
      request = {
        ...request,
        organization_detail: {
          ...request.organization_detail,
          privacy_policy_url: organization?.privacy_policy_url,
        },
      };
    }
    if (
      organization?.merchant_detail?.key?.trim() &&
      organization?.merchant_detail?.secret?.trim() &&
      !organization?.merchant_detail?.resource?.trim()
    ) {
      request = {
        ...request,
        merchant_detail: {
          key: organization?.merchant_detail?.key || '',
          secret: organization?.merchant_detail?.secret || '',
          resource: organization?.merchant_detail?.resource || '',
        },
      };

      if (showGeotab) {
        request = {
          ...request,
          geotab_credentials: {
            database_name: organization?.geotab_credentials?.database_name || '',
            user_name: organization?.geotab_credentials?.user_name || '',
            password: organization.geotab_credentials?.password || '',
            // path: ,
          },
        };
      }
    }

    if (showCar) {
      request = {
        ...request,
        car_details: {
          user_name: organization?.car_details?.user_name?.trim() || '',
          password: organization?.car_details?.password || '',
        },
      };
    }

    dispatch(AddOrganizationAPI({ request }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('organization.messages.organization_added'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleEditOrganization = () => {
    setLoader(true);
    let reqAddress: IAddress = {
      address1: organization?.fiscal_address?.address1 || '',
      country: organization?.fiscal_address?.country || '',
      state: organization?.fiscal_address?.state || '',
      city: organization?.fiscal_address?.city || '',
      zipcode: organization?.fiscal_address?.zipcode || '',
    };
    if (organization?.fiscal_address?.address2?.trim()) {
      reqAddress = { ...reqAddress, address2: organization.fiscal_address.address2 || '' };
    }

    let request: IEditOrganizationRequest = {
      organization_detail: {
        org_id: organization?._id || '',
        organization_name: organization?.organization_name || '',
        fiscal_name: organization?.fiscal_name || '',
        email: organization?.email,
        country_code: `+${organization?.country_code.replaceAll('+', '')}`,
        mobile_no: organization?.mobile_no,
        fiscal_address: reqAddress || null,
        parking_ids: organization?.parking_details.map(parking => parking?._id || ''),
      },
    };
    if (organization?.geotab_credentials) {
      request = {
        ...request,
        geotab_credentials: organization?.geotab_credentials,
      };
    }
    if (organization?.logo_key?.trim()) {
      request = {
        ...request,
        organization_detail: { ...request.organization_detail, logo: organization?.logo_key },
      };
    }
    if (organization?.contact_us_url?.trim()) {
      request = {
        ...request,
        organization_detail: {
          ...request.organization_detail,
          contact_us_url: organization?.contact_us_url,
        },
      };
    }
    if (organization?.privacy_policy_url?.trim()) {
      request = {
        ...request,
        organization_detail: {
          ...request.organization_detail,
          privacy_policy_url: organization?.privacy_policy_url,
        },
      };
    }
    if (
      organization?.merchant_detail?.key?.trim() &&
      organization?.merchant_detail?.secret?.trim() &&
      organization?.merchant_detail?.resource?.trim()
    ) {
      request = {
        ...request,
        merchant_detail: {
          key: organization?.merchant_detail?.key || '',
          secret: organization?.merchant_detail?.secret || '',
          resource: organization?.merchant_detail?.resource || '',
        },
      };
    }
    if (showGeotab) {
      request = {
        ...request,
        geotab_credentials: {
          database_name: organization?.geotab_credentials?.database_name || '',
          user_name: organization?.geotab_credentials?.user_name || '',
          password: organization.geotab_credentials?.password || '',
          // path: ,
        },
      };
      delete request.organization_detail.geotab_credentials;
    }

    request = {
      ...request,
      car_details: showCar
        ? {
            user_name: organization?.car_details?.user_name?.trim() || '',
            password: organization?.car_details?.password || '',
          }
        : null,
    };

    dispatch(EditOrganizationAPI({ request, org_id: organization?._id || '' }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('organization.messages.organization_updated'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleSubmit = async () => {
    if (!isValid()) {
      return;
    }

    // A parking still open for editing (e.g. a new CAR branch ID) is saved
    // first, so its changes are not lost when only the organization is saved
    if (formFor === 'EDIT' && parkingAddress?.isEdited && parkingAddress?._id) {
      try {
        setLoader(true);
        await dispatch(
          editOrganizationParkingDetails({ request: getParkingEditRequest(parkingAddress) }),
        );
        setParkingAddress(null);
      } catch (error) {
        setLoader(false);
        return;
      }
    }

    formFor === 'EDIT' ? handleEditOrganization() : handleAddOrganization();
  };
  const getStateList = (key: 'fiscal_address' | 'parking_address') => {
    let selectedCountry = null;
    if (key === 'parking_address') {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === parkingAddress?.country,
      );
    } else {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === organization[key]?.country,
      );
    }

    return stateList(selectedCountry?.isoCode) || [];
  };
  const getSelectedState = (key: 'fiscal_address' | 'parking_address') => {
    let selectedCountry = null;
    if (key === 'parking_address') {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === parkingAddress?.country,
      );
    } else {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === organization[key]?.country,
      );
    }
    return (
      stateList(selectedCountry?.isoCode).find(
        (data: { name: string }) =>
          data.name ===
          (key === 'parking_address' ? parkingAddress?.state : organization[key]?.state),
      ) || null
    );
  };
  const getCityList = (key: 'fiscal_address' | 'parking_address') => {
    const selectedState = getSelectedState(key);
    return cityList(selectedState?.countryCode, selectedState?.isoCode) || [];
  };
  const getSelectedCity = (key: 'fiscal_address' | 'parking_address') => {
    const selectedState = getSelectedState(key);
    return (
      cityList(selectedState?.countryCode, selectedState?.isoCode).find(
        (data: { name: string }) =>
          data.name ===
          (key === 'parking_address' ? parkingAddress?.city : organization[key]?.city),
      ) || null
    );
  };
  const setLogoData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event?.target.files?.length) {
        setLoader(true);
        const file = event?.target?.files[0];
        const params: IUploadImageRequestParam = {
          request: {
            file_name: event?.target.files[0]?.name || '',
            file_size: event?.target.files[0]?.size || 0,
            file_type: event?.target.files[0]?.type || '',
          },
          file: file,
        };
        dispatch(getOrganizationUploadLogoLocation({ params }))
          .then((response: { key: string; download_url: string; url: string }) => {
            setOrganizations({ ...organization, logo_key: response.key });
            setLogo({ file: file, url: window.URL.createObjectURL(file) });
            setLoader(false);
          })
          .catch(() => {
            setLoader(false);
          });
      }
    } catch (error) {
      setLoader(false);
    }
  };
  const handleUploadLogo = () => {
    const fileElement = logoRef.current;
    fileElement?.click();
  };
  const handleParkingAddress = (field: string, value: string) => {
    setParkingAddress({
      _id: parkingAddress?._id,
      address1: parkingAddress?.address1 || '',
      address2: parkingAddress?.address2 || '',
      city: parkingAddress?.city || '',
      state: parkingAddress?.state || '',
      zipcode: parkingAddress?.zipcode || '',
      country: parkingAddress?.country || '',
      isEdited: parkingAddress?.isEdited ? true : false,
      address_title: parkingAddress?.address_title || '',
      car_branch_id: parkingAddress?.car_branch_id ?? '',
      [field]: value || '',
    });
  };
  const getParkingEditRequest = (updatedParking: IAddress) => {
    const request: IEditOrganizationParkinfParams = {
      address_title: updatedParking.address_title || '',
      parking_id: updatedParking?._id || '',
      address1: updatedParking?.address1 || '',
      address2: updatedParking?.address2 || '',
      city: updatedParking?.city || '',
      state: updatedParking?.state || '',
      country: updatedParking?.country || '',
      zipcode: updatedParking?.zipcode || '',
      // An empty value removes the CAR branch id
      car_branch_id: updatedParking?.car_branch_id ? Number(updatedParking.car_branch_id) : null,
    };
    if (!updatedParking?.address2?.trim()) {
      delete request?.address2;
    }
    return request;
  };
  const editOrganizationParking = (updatedParking: IAddress) => {
    try {
      const request = getParkingEditRequest(updatedParking);
      setLoader(true);
      dispatch(editOrganizationParkingDetails({ request }))
        .then(() => {
          const updateParkingList = organization?.parking_details?.map(park =>
            park?._id === updatedParking?._id ? { ...updatedParking } : { ...park },
          );
          setOrganizations({ ...organization, parking_details: [...updateParkingList] });
          setParkingAddress(null);
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
      return Promise.reject(error);
    }
  };
  const addOrganizationParking = (updatedParking: IAddress) => {
    let request: IEditOrganizationParkinfParams = {
      address_title: updatedParking?.address_title || '',
      address1: updatedParking?.address1 || '',
      city: updatedParking?.city || '',
      state: updatedParking?.state || '',
      country: updatedParking?.country || '',
      zipcode: updatedParking?.zipcode || '',
    };
    if (updatedParking?.address2?.trim()) {
      request = { ...request, address2: updatedParking?.address2 || '' };
    }
    if (updatedParking?.car_branch_id) {
      request = { ...request, car_branch_id: Number(updatedParking.car_branch_id) };
    }
    dispatch(
      addOrganizationParkingDetails({
        request: request,
      }),
    )
      .then((response: IOrganizationAddress) => {
        setOrganizations({
          ...organization,
          parking_details: [
            ...organization?.parking_details,
            {
              _id: response?._id || '',
              address_title: response?.address_title || '',
              address1: response?.address1 || '',
              address2: response?.address2 || '',
              city: response?.city || '',
              state: response?.state || '',
              country: response?.country || '',
              zipcode: response?.zipcode || '',
              car_branch_id: response?.car_branch_id,
              is_deleted: response?.is_deleted,
            },
          ],
        });
        setParkingAddress(null);
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };
  const handleAddParkingAddress = async () => {
    if (!parkingAddress?.address_title?.trim()) {
      toast.error(t('organization.errors.parking_address_title_required'));
      return;
    } else if (!parkingAddress?.address1?.trim()) {
      toast.error(t('organization.errors.parking_address_line1_required'));
      return;
    } else if (!parkingAddress?.country?.trim()) {
      toast.error(t('organization.errors.parking_address_country_required'));
      return;
    } else if (!parkingAddress?.state?.trim()) {
      toast.error(t('organization.errors.parking_address_state_required'));
      return;
    } else if (!parkingAddress?.city?.trim()) {
      toast.error(t('.errors.parking_address_city_required'));
      return;
    } else if (!parkingAddress?.zipcode) {
      toast.error(t('organization.errors.parking_address_zipcode_required'));
      return;
    }
    if (parkingAddress.isEdited) {
      editOrganizationParking(parkingAddress);
    } else {
      addOrganizationParking(parkingAddress);
    }
  };
  const handleDeleteParkingAddress = async (id: string) => {
    let updatedParkingData = [...organization?.parking_details];
    setLoader(true);
    if (organization?._id && organization?.parking_details?.find(park => park._id === id)) {
      await dispatch(
        removeOrganizationParking({
          params: { organization_id: organization?._id, parking_id: id },
        }),
      ).catch(() => setLoader(false));
    }
    updatedParkingData = updatedParkingData.filter(data => data?._id !== id);
    setOrganizations({
      ...organization,
      parking_details: updatedParkingData,
    });
    setLoader(false);
  };

  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{ label: formFor === 'EDIT' ? t('SAVE_CHANGES') : t('ADD_ORGANIZATION') }}
    >
      <div className="flex flex-wrap p-6">
        <div className="flex items-center w-1/2 px-3 pb-4">
          <div className="w-28 h-28 flex items-center justify-center rounded-full bg-grey2">
            {!logo?.url ? (
              <label className="text-base font-medium text-white">{t('NO_IMG')}</label>
            ) : (
              <img
                src={logo?.url}
                alt="Org-LOGO"
                className="h-full w-full rounded-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center justify-center flex-1">
            <input
              type="file"
              className="hidden"
              ref={logoRef}
              onChange={event => setLogoData(event)}
              accept="image/*"
            />
            <PrimaryButtom
              label={
                !logo?.url
                  ? t('organization.labels.upload_image')
                  : t('organization.labels.change_image')
              }
              onClick={() => handleUploadLogo()}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.organization_name')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('organization.placeholders.organization_name')}
              value={organization?.organization_name}
              onChange={val => handleChange('organization_name', val)}
              error={error?.organization_name || ''}
            />
          </div>
        </div>
        <div className="flex  flex-wrap items-center w-full bg-grey4 p-4 rounded-4 mb-2">
          <label className="text-base font-semibold w-full mb-4">
            {`${t('organization.labels.payment_keys')} (${t('OPTIONAL')})`}
          </label>
          <div className="w-1/3 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('organization.labels.key')}</label>
              <InputField
                placeholder={t('organization.placeholders.key')}
                value={organization?.merchant_detail?.key || ''}
                onChange={val =>
                  setOrganizations({
                    ...organization,
                    merchant_detail: { ...organization?.merchant_detail, key: val },
                  })
                }
                error={error?.key || ''}
              />
            </div>
          </div>
          <div className="w-1/3 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('organization.labels.secret')}</label>
              <InputField
                placeholder={t('organization.placeholders.secret')}
                value={organization?.merchant_detail?.secret || ''}
                onChange={val =>
                  setOrganizations({
                    ...organization,
                    merchant_detail: { ...organization?.merchant_detail, secret: val },
                  })
                }
                error={error?.secret || ''}
              />
            </div>
          </div>
          <div className="w-1/3 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('organization.labels.resource')}</label>
              <InputField
                placeholder={t('organization.placeholders.resource')}
                value={organization?.merchant_detail?.resource || ''}
                onChange={val =>
                  setOrganizations({
                    ...organization,
                    merchant_detail: { ...organization?.merchant_detail, resource: val },
                  })
                }
                error={error?.resource || ''}
              />
            </div>
          </div>
        </div>
        {organization?.geotab_credentials ? (
          <label className=" italic font-semibold">{t('GEOTAB_ADDED')}</label>
        ) : (
          <div className="w-full flex items-center mb-4">
            <input
              type="checkbox"
              id="geotab"
              checked={showGeotab}
              onChange={() => setShowGeotab(prev => !prev)}
              className="mr-2"
            />
            <label htmlFor="geotab" className="font-medium text-black">
              {t('administrator_roles.addEdit.addGeotab')}
            </label>
          </div>
        )}

        {(showGeotab || organization.geotab_credentials) && (
          <div className="flex flex-wrap items-center w-full bg-grey4 p-4 rounded-4 mb-2">
            <label className="text-base font-semibold w-full mb-4">
              {`${t('organization.labels.database')}`}
            </label>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('organization.labels.database_name')}
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  placeholder={t('administrator_roles.addEdit.databasePlaceholder')}
                  value={organization?.geotab_credentials?.database_name || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      geotab_credentials: {
                        ...organization?.geotab_credentials,
                        database_name: val,
                      },
                    })
                  }
                  error={error?.geotab_credentials?.database_name || ''}
                />
              </div>
            </div>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('administrator_roles.addEdit.username')}
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  placeholder={t('administrator_roles.addEdit.usernamePalceholder')}
                  value={organization?.geotab_credentials?.user_name || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      geotab_credentials: { ...organization?.geotab_credentials, user_name: val },
                    })
                  }
                  error={error?.geotab_credentials?.user_name || ''}
                />
              </div>
            </div>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('administrator_roles.addEdit.password')}
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  placeholder={t('administrator_roles.addEdit.passwordPlaceholder')}
                  value={organization?.geotab_credentials?.password || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      geotab_credentials: { ...organization?.geotab_credentials, password: val },
                    })
                  }
                  error={error?.geotab_credentials?.password || ''}
                />
              </div>
            </div>
            {/* <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  path
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  placeholder="path"
                  value={organization?.merchant_detail?.private_key || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      merchant_detail: { ...organization?.merchant_detail, private_key: val },
                    })
                  }
                  error={error?.geotab_credentials?.path || ''}
                />
              </div>
            </div> */}
          </div>
        )}
        <div className="w-full flex items-center mb-4">
          <input
            type="checkbox"
            id="car"
            checked={showCar}
            onChange={() => setShowCar(prev => !prev)}
            className="mr-2"
          />
          <label htmlFor="car" className="font-medium text-black">
            {t('organization.labels.use_car')}
          </label>
        </div>
        {showCar && (
          <div className="flex flex-wrap items-center w-full bg-grey4 p-4 rounded-4 mb-2">
            <label className="text-base font-semibold w-full mb-4">
              {t('organization.labels.car_solution')}
            </label>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('organization.labels.car_user_name')}
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  placeholder={t('organization.placeholders.car_user_name')}
                  value={organization?.car_details?.user_name || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      car_details: { ...organization?.car_details, user_name: val },
                    })
                  }
                  error={error?.car_details?.user_name || ''}
                />
              </div>
            </div>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black w-full">
                  {t('organization.labels.car_password')}
                  <span className="text-red-500 ml-1 text-base font-bold">*</span>
                </label>
                <InputField
                  type="password"
                  placeholder={t('organization.placeholders.car_password')}
                  value={organization?.car_details?.password || ''}
                  onChange={val =>
                    setOrganizations({
                      ...organization,
                      car_details: { ...organization?.car_details, password: val },
                    })
                  }
                  error={error?.car_details?.password || ''}
                />
              </div>
            </div>
          </div>
        )}
        <div className="w-full px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.fiscal_name')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('organization.placeholders.fiscal_name')}
              value={organization?.fiscal_name}
              onChange={val => handleChange('fiscal_name', val)}
              error={error?.fiscal_name || ''}
            />
          </div>
        </div>
        <div className="w-full px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.fiscal_number')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('organization.placeholders.fiscal_number')}
              value={organization.fiscal_number}
              onChange={val => handleChange('fiscal_number', val)}
              error={error?.fiscal_number || ''}
              disabled={formFor === 'EDIT'}
            />
          </div>
        </div>

        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('EMAIL')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <InputField
              placeholder={t('organization.placeholders.email_address')}
              value={organization?.email}
              onChange={val => handleChange('email', val)}
              error={error?.email || ''}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.telephone')}
              <span className="text-red-500 ml-1 text-base font-bold">*</span>
            </label>
            <PhoneInputComponent
              value={organization?.mobile_no}
              countryCode={organization?.country_code}
              onChange={data => handleMobileNumber(data)}
              error={error?.mobile_no}
            />
          </div>
        </div>
        <div className="w-full px-3">
          <label className="font-medium text-sm text-black w-full">
            {t('organization.labels.address')}
            <span className="text-red-500 ml-1 text-base font-bold">*</span>
          </label>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.address_line1')}
              value={organization?.fiscal_address?.address1 || ''}
              onChange={val => handleChange('fiscal_address', val, 'address1')}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.address_line2')}
              value={organization?.fiscal_address?.address2 || ''}
              onChange={val => handleChange('fiscal_address', val, 'address2')}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.country')}
              selected={
                countryList.find(
                  (data: { name: string }) => data.name === organization?.fiscal_address?.country,
                ) || { id: '00', name: organization?.fiscal_address?.country || '' }
              }
              setSelected={data => handleChange('fiscal_address', data?.name, 'country')}
              options={countryList.filter(
                coun => coun?.name.toLowerCase().startsWith(dropdownSearch?.toLowerCase()),
              )}
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleChange('fiscal_address', val, 'country');
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.state')}
              selected={
                getSelectedState('fiscal_address') || {
                  id: '00',
                  name: organization?.fiscal_address?.state || '',
                }
              }
              setSelected={data => handleChange('fiscal_address', data?.name, 'state')}
              options={getStateList('fiscal_address').filter(
                state => state?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
              )}
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleChange('fiscal_address', val, 'state');
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.city')}
              selected={
                getSelectedCity('fiscal_address') || {
                  id: '00',
                  name: organization?.fiscal_address?.city || '',
                }
              }
              setSelected={data => handleChange('fiscal_address', data?.name, 'city')}
              options={getCityList('fiscal_address').filter(
                cit => cit?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
              )}
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleChange('fiscal_address', val, 'city');
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.postal_code')}
              value={organization.fiscal_address?.zipcode || ''}
              onChange={val => {
                if (val && !onlyNumbers.test(val)) {
                  return;
                }
                handleChange('fiscal_address', val, 'zipcode');
              }}
            />
          </div>
        </div>
        <div className="w-full px-3">
          {error.fiscal_address && (
            <p className=" font-regular text-xs text-red-500">{error.fiscal_address}</p>
          )}
        </div>
        <div className="w-full px-3">
          <label className="font-medium text-sm text-black w-full">
            {t('organization.labels.parking_address')}
            <span className="text-red-500 ml-1 text-base font-bold">*</span>
          </label>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.title')}
              value={parkingAddress?.address_title || ''}
              onChange={val => handleParkingAddress('address_title', val)}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          {/* CAR branch of this parking; only used when the organization uses CAR */}
          <div className="mb-2 flex items-center gap-2">
            <label className="font-medium text-sm text-black whitespace-nowrap">
              {t('organization.labels.car_branch_id')}
            </label>
            <InputField
              placeholder={t('organization.placeholders.car_branch_id')}
              value={String(parkingAddress?.car_branch_id ?? '')}
              onChange={val => {
                if (val && !onlyNumbers.test(val)) {
                  return;
                }
                handleParkingAddress('car_branch_id', val);
              }}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.address_line1')}
              value={parkingAddress?.address1 || ''}
              onChange={val => handleParkingAddress('address1', val)}
            />
          </div>
        </div>
        <div className="w-1/2 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.address_line2')}
              value={parkingAddress?.address2 || ''}
              onChange={val => handleParkingAddress('address2', val)}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.country')}
              selected={
                countryList.find(
                  (data: { name: string }) => data.name === parkingAddress?.country,
                ) || { id: '00', name: parkingAddress?.country || '' }
              }
              setSelected={data => handleParkingAddress('country', data?.name)}
              options={countryList.filter(
                coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
              )}
              dropdownPlacement="TOP"
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleParkingAddress('country', val);
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.state')}
              selected={
                getSelectedState('parking_address') || {
                  id: '00',
                  name: parkingAddress?.state || '',
                }
              }
              setSelected={data => handleParkingAddress('state', data?.name)}
              options={getStateList('parking_address').filter(
                coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
              )}
              dropdownPlacement="TOP"
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleParkingAddress('state', val);
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <Select
              placeholder={t('organization.labels.city')}
              selected={
                getSelectedCity('parking_address') || { id: '00', name: parkingAddress?.city || '' }
              }
              setSelected={data => handleParkingAddress('city', data?.name)}
              options={getCityList('parking_address').filter(
                coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
              )}
              dropdownPlacement="TOP"
              isSearchable
              search={dropdownSearch}
              setSearch={val => {
                setDropdownSearch(val);
                handleParkingAddress('city', val);
              }}
            />
          </div>
        </div>
        <div className="w-1/4 px-3">
          <div className="mb-2">
            <InputField
              placeholder={t('organization.labels.postal_code')}
              value={parkingAddress?.zipcode || ''}
              onChange={val => {
                if (val && !onlyNumbers.test(val)) {
                  return;
                }
                handleParkingAddress('zipcode', val);
              }}
            />
          </div>
        </div>
        <div className="w-full px-3">
          <div className="flex flex-wrap -mx-3">
            <div className="w-1/2 px-3"></div>
            <div className="w-1/2 px-3 text-right">
              <div className="flex justify-end">
                <PrimaryDarkButton
                  label={`${
                    parkingAddress?.isEdited
                      ? t('organization.labels.save_changes')
                      : t('organization.labels.add_address')
                  }`}
                  onClick={() => handleAddParkingAddress()}
                  height="h-7"
                  text="text-sm"
                  width="w-1/3"
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="flex flex-wrap rounded-2 mt-3 bg-bg-50 p-4 mb-3 border-1.5">
                {(organization?.parking_details || []).filter(park => !park.is_deleted).length >
                0 ? (
                  (organization?.parking_details || [])
                    .filter(park => !park.is_deleted)
                    .map((parking_add, index) => (
                      <div
                        className="flex flex-col w-1/3 p-3"
                        key={`${parking_add?._id || parking_add?.address_title}-${index}`}
                      >
                        <div className="flex items-center w-full">
                          <label className="text-base font-medium flex-1">
                            {parking_add?.address_title || index + 1}
                          </label>
                          <div className="flex items-center gap-x-1">
                            <EditIcon
                              className="cursor-pointer"
                              height={16}
                              width={16}
                              onClick={() => setParkingAddress({ ...parking_add, isEdited: true })}
                            />
                            <DeleteIcon
                              color="red"
                              className="cursor-pointer"
                              height={16}
                              width={16}
                              onClick={() => handleDeleteParkingAddress(parking_add?._id || '')}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col w-full gap-x-2 ">
                          <div className="flex items-center flex-wrap break-words text-sm text-black2 w-full">{`${parking_add?.address1}, ${
                            parking_add?.address2 ? `${parking_add?.address2},` : ''
                          } ${parking_add?.city}, ${parking_add?.state}, ${parking_add?.country} - ${parking_add?.zipcode}.`}</div>
                          {parking_add?.car_branch_id ? (
                            <div className="text-sm text-grey3 w-full">
                              {`${t('organization.labels.car_branch_id')}: ${parking_add.car_branch_id}`}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="flex items-center justify-center py-4 px-1 w-full rounded-2 bg-bg-50">
                    <label className="text-base font-medium text-grey3 text-center w-full mx-auto">
                      {t('organization.labels.no_parking_address')}
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full px-3 flex justify-between">
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.concessionaire_contact')}
            </label>
            <InputField
              placeholder={t('URL_PLACEHOLER')}
              value={organization?.contact_us_url || ''}
              onChange={val => handleChange('contact_us_url', val)}
              error={error?.contact_us_url || ''}
            />
          </div>
          <div className="mb-3">
            <label className="font-medium text-sm text-black w-full">
              {t('organization.labels.privacy_policy_url')}
            </label>
            <InputField
              placeholder={t('URL_PLACEHOLER')}
              value={organization?.privacy_policy_url || ''}
              onChange={val => handleChange('privacy_policy_url', val)}
              error={error?.privacy_policy_url || ''}
            />
          </div>
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddOrganization;
